import { Button, InlineLoading } from '@carbon/react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { capitalize, startCase } from 'lodash-es';
import {
  isProductionStreamConfigModified,
  getNextMaxMonthsOnline,
} from '../utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MineralType } from 'types/minerals';
import styles from './production-stream-configs.module.scss';
import { Reset, Replicate, Delete } from '@carbon/react/icons';
import { AddModels } from './add-models';
import { useScenarioForm } from '../contexts';
import {
  ProductionStreamConfigData,
  StreamConfigKey,
  STREAM_CONFIG_KEYS,
} from '../types';
import { ModelType } from 'types/dca-scenarios-api/responses';
import { CommonFields } from './common-fields';
import { ModelParameters } from './model-parameters';
import commonStyles from '../common.module.scss';
import { formatNumber } from 'utils/number';
import { ProductionStreamConfigSkeleton } from '../config-skeleton';
import { createResourceAtom } from 'lib/atoms';
import { atom, useAtom, useAtomValue } from 'jotai';
import { scenarioFormDefaultValues } from '../default-values';
import { deleteProductionStreamConfigAtom } from '../atoms';
import { DeleteModal } from '../delete-modal';

const FALLBACK_ATOM = atom(null);

const STREAMS: MineralType[] = ['oil', 'gas', 'water'];

export const ProductionSteamConfigs = () => {
  const { form, selectedConfig, setFieldBaseline } = useScenarioForm();

  const productionStreamConfigsHref =
    selectedConfig?.resources?.productionStreamConfigs?.href ?? null;

  const resourceAtom = useMemo(() => {
    if (!productionStreamConfigsHref) return FALLBACK_ATOM;
    return createResourceAtom<any>(productionStreamConfigsHref);
  }, [productionStreamConfigsHref]);

  const resource = useAtomValue(resourceAtom);

  useEffect(() => {
    if (resource && resource.data) {
      const results = resource.data.results as ProductionStreamConfigData[];
      queueMicrotask(() => {
        for (const stream of STREAMS) {
          const key = STREAM_CONFIG_KEYS[stream];

          const streamConfigs: ProductionStreamConfigData[] = [];

          results.forEach((config) => {
            if (config.stream === stream) {
              streamConfigs.push(config);
            }
          });

          setFieldBaseline(key, streamConfigs);
        }
      });
    } else if (resource === null && selectedConfig?.id) {
      queueMicrotask(() => {
        for (const stream of STREAMS) {
          const key = STREAM_CONFIG_KEYS[stream];
          const fallback = scenarioFormDefaultValues[key];
          setFieldBaseline(key, fallback);
        }
      });
    }
  }, [resource, setFieldBaseline, form, selectedConfig?.id]);

  return (
    <>
      {STREAMS.map((stream) => (
        <ProductionStreamSection key={stream} stream={stream} />
      ))}
    </>
  );
};

interface IndexedConfig {
  config: ProductionStreamConfigData;
  originalIndex: number;
}

interface ProductionStreamSectionProps {
  stream: MineralType;
}

const MODEL_TYPE_ORDER: Record<ModelType, number> = {
  unbiased: 1,
  ramp_up: 2,
  early_time: 3,
  biased: 4,
};

const ProductionStreamSection = ({ stream }: ProductionStreamSectionProps) => {
  const streamKey = STREAM_CONFIG_KEYS[stream];
  const { form } = useScenarioForm();

  return (
    <form.Subscribe
      selector={(state) => {
        const configs = state.values[streamKey];
        if (!configs) return null;

        return configs
          .map((config: ProductionStreamConfigData, originalIndex: number) => ({
            config,
            originalIndex,
          }))
          .sort((a: IndexedConfig, b: IndexedConfig) => {
            const typeComparison =
              MODEL_TYPE_ORDER[a.config.modelType] -
              MODEL_TYPE_ORDER[b.config.modelType];

            if (typeComparison === 0) {
              return a.config.maxMonthsOnline - b.config.maxMonthsOnline;
            }

            return typeComparison;
          });
      }}
    >
      {(indexedConfigs) => {
        if (!indexedConfigs) {
          return <ProductionStreamConfigSkeleton stream={stream} />;
        }

        return (
          <ProductionSteamConfig
            stream={stream}
            streamKey={streamKey}
            indexedConfigs={indexedConfigs}
          />
        );
      }}
    </form.Subscribe>
  );
};

interface ProductionSteamConfigProps {
  stream: MineralType;
  streamKey: StreamConfigKey;
  indexedConfigs: IndexedConfig[];
}

const ProductionSteamConfig = ({
  stream,
  streamKey,
  indexedConfigs,
}: ProductionSteamConfigProps) => {
  const [expanded, setExpanded] = useState(true);
  const [deleteModalIndex, setDeleteModalIndex] = useState<number | null>(null);
  const {
    form,
    defaultValues,
    baselines,
    updateFieldDefault,
    setFieldBaseline,
  } = useScenarioForm();

  const [deleteProductionStreamConfigState, deleteProductionStreamConfig] =
    useAtom(deleteProductionStreamConfigAtom);

  const handleStreamReset = useCallback(() => {
    let baselineConfigs = baselines[streamKey];
    if (!baselineConfigs || baselineConfigs.length === 0) {
      baselineConfigs = scenarioFormDefaultValues[streamKey];
    }

    form.setFieldValue(streamKey, baselineConfigs);
    updateFieldDefault(streamKey, baselineConfigs);
  }, [form, baselines, streamKey, updateFieldDefault]);

  const configs = useMemo(
    () => indexedConfigs.map((ic) => ic.config),
    [indexedConfigs]
  );

  const handleDuplicate = (modelType: string, configIndex: number) => {
    const nextMaxMonthsOnline = getNextMaxMonthsOnline(configs, modelType);
    const currentConfig = form.state.values[streamKey]?.[configIndex];
    if (!currentConfig) return;
    const duplicatedConfig: ProductionStreamConfigData = {
      ...currentConfig,
      id: null,
      maxMonthsOnline: nextMaxMonthsOnline,
    };

    (form as any).pushFieldValue(streamKey, duplicatedConfig);
    updateFieldDefault(streamKey, [
      ...(defaultValues[streamKey] || []),
      duplicatedConfig,
    ]);
  };

  const handleReset = (configIndex: number) => {
    const defaultConfig = defaultValues[streamKey]?.[configIndex];
    if (!defaultConfig) return;
    const current = form.state.values[streamKey];
    if (!current) return;
    const resetConfigs = current.map(
      (c: ProductionStreamConfigData, i: number) => {
        return i === configIndex ? defaultConfig : c;
      }
    );
    form.setFieldValue(streamKey, resetConfigs);
  };

  const handleDeleteArrayElement = (configIndex: number) => {
    const current = form.state.values[streamKey] || [];
    const updatedConfigs = current.filter(
      (_: ProductionStreamConfigData, i: number) => i !== configIndex
    );
    setFieldBaseline(streamKey, updatedConfigs);
  };

  const handleDelete = (configIndex: number, href?: string) => {
    if (href) {
      deleteProductionStreamConfig({
        url: href,
        type: 'DELETE',
        data: {},
        onSuccess: () => {
          handleDeleteArrayElement(configIndex);
        },
      });
    } else {
      handleDeleteArrayElement(configIndex);
    }
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <AccordionSummary>
          <h2 className={commonStyles.configFormTitle}>{`${capitalize(
            stream
          )} Model Config`}</h2>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.configFormContainer}>
            <AddModels
              stream={stream}
              streamKey={streamKey}
              onSectionReset={handleStreamReset}
              disabled={deleteProductionStreamConfigState.loading}
              configs={configs as ProductionStreamConfigData[]}
            />

            {indexedConfigs?.map(({ config, originalIndex }) => {
              return (
                <div key={originalIndex}>
                  <div>
                    <form.Subscribe
                      selector={(state) =>
                        state.values[streamKey]?.[originalIndex]
                      }
                    >
                      {(currentConfig) => {
                        const defaultConfig =
                          defaultValues[streamKey]?.[originalIndex];
                        const isModified =
                          !!defaultConfig &&
                          !!currentConfig &&
                          isProductionStreamConfigModified(
                            currentConfig,
                            defaultConfig
                          );

                        return (
                          <Accordion>
                            <AccordionSummary className={styles.modelTypeTitle}>
                              <ModelAccordionTitle
                                modelType={config.modelType}
                                maxMonths={config.maxMonthsOnline}
                              />
                            </AccordionSummary>
                            <AccordionDetails>
                              <div className={styles.modelDetails}>
                                <CommonFields
                                  streamKey={streamKey}
                                  configIndex={originalIndex}
                                  config={config}
                                />
                                <ModelParameters
                                  streamKey={streamKey}
                                  configIndex={originalIndex}
                                  config={config}
                                />

                                <div className={styles.buttonContainer}>
                                  <Button
                                    size="md"
                                    kind="tertiary"
                                    onClick={() => {
                                      handleDuplicate(
                                        config.modelType,
                                        originalIndex
                                      );
                                    }}
                                    disabled={
                                      deleteProductionStreamConfigState.loading
                                    }
                                    renderIcon={Replicate}
                                  >
                                    Duplicate
                                  </Button>
                                  <Button
                                    size="md"
                                    kind="tertiary"
                                    onClick={() => handleReset(originalIndex)}
                                    renderIcon={Reset}
                                    disabled={
                                      !isModified ||
                                      deleteProductionStreamConfigState.loading
                                    }
                                  >
                                    Reset
                                  </Button>
                                  <Button
                                    size="md"
                                    kind="danger--tertiary"
                                    onClick={() => {
                                      setDeleteModalIndex(originalIndex);
                                    }}
                                    renderIcon={Delete}
                                    disabled={
                                      deleteProductionStreamConfigState.loading
                                    }
                                  >
                                    {deleteProductionStreamConfigState.loading ? (
                                      <InlineLoading />
                                    ) : (
                                      'Delete'
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </AccordionDetails>
                          </Accordion>
                        );
                      }}
                    </form.Subscribe>
                  </div>
                  <DeleteModal
                    open={deleteModalIndex === originalIndex}
                    onClose={() => {
                      setDeleteModalIndex(null);
                    }}
                    onDelete={() => {
                      handleDelete(originalIndex, config.href);
                      setDeleteModalIndex(null);
                    }}
                    title={`Delete ${startCase(config.modelType)}`}
                    description={`Are you sure you want to Delete the ${startCase(
                      config.modelType
                    )} from ${startCase(stream)} Model Config?`}
                  />
                </div>
              );
            })}
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

interface ModelAccordionTitleProps {
  modelType: string;
  maxMonths: number;
}

const ModelAccordionTitle = ({
  modelType,
  maxMonths,
}: ModelAccordionTitleProps) => {
  return (
    <div className={styles.modelAccordionTitle}>
      <div className={styles.modelAccordionTitleContainer}>
        <p className={styles.modelAccordionTitleText}>{startCase(modelType)}</p>
      </div>
      <p className={styles.modelAccordionTitleMaxMonths}>
        Max Months:{' '}
        {formatNumber({
          value: maxMonths,
          minPrecision: 0,
          includeCommas: true,
        })}
      </p>
    </div>
  );
};
